// src/components/patient/PftHistoryEditor.js
//
// The patient page's PFT section (flag PFT_HISTORY_ENABLED): every dated PFT
// the patient has, newest first, always visible whatever the diagnosis. The
// model and every rule about saving live in src/services/pftTests.js.
//
// A patient's existing PFT values show as their first test ("Existing values"),
// read from the same fields as before, so nothing already entered disappears.
// Removing a test hides it (removedAt) and keeps it; it can be restored.
//
// Soft checks (2026-10-20, rules in services/pftSoftChecks.js): a value only one
// extraction reading found is listed at the top of its PFT date, with "Review" (opens the
// PDF) and a "Verified" tick. On the patient page the tick is saved with the page's Save;
// on PFT Uploads (onVerify) it is saved at once, on the import, and comes along to RegX.

import { useEffect, useRef, useState } from "react";
import { Plus, FileText, Trash2, RotateCcw, ChevronDown, ExternalLink } from "lucide-react";
import {
  isRemoved,
  newManualTest,
  newestTest,
  PFT_FIELD_GROUPS,
  PFT_GROUPED_KEYS,
  PFT_NOTE_FIELDS,
  otherValueKeys,
  PFT_TEST_FIELDS,
  visibleTestsNewestFirst,
} from "../../services/pftTests";
import { openPftPdf } from "../../services/pftPdfService";
import { softChecksOf, withVerification } from "../../services/pftSoftChecks";
import { nameTokens } from "../../services/patientDuplicateMatch";

const field = (key) => PFT_TEST_FIELDS.find((f) => f.key === key);
const PAIRS = [
  { label: "FEV1/FVC Ratio (Actual)", pre: "fev1fvcPre", post: "fev1fvcPost" },
  { label: "FEV1 % Predicted", pre: "fev1Pre", post: "fev1Post" },
  { label: "FVC % Predicted", pre: "fvcPre", post: "fvcPost" },
];
// Text (not number) fields among the grouped ones.
const TEXT_KEYS = new Set(
  PFT_TEST_FIELDS.filter((f) => /Unit$/.test(f.key) || f.type === "text").map((f) => f.key).concat([
    "referringPhysician", "interpretingPhysician", "smokingStatus", "bronchodilator", "qualityGrade",
    "referenceEquations", "methacholinePc20", "methacholinePd20", "height", "weight",
  ]),
)

const blank = (v) => v === undefined || v === null || String(v).trim() === "";
const NO_BOXES = new Map();
const differentName = (a, b) => nameTokens(a).sort().join(" ") !== nameTokens(b).sort().join(" ");
const ISO = /^\d{4}-\d{2}-\d{2}$/;

const SOURCE_BADGE = {
  "pft-upload": { text: "From uploaded PDF", cls: "bg-sky-500/10 border-sky-400/30 text-sky-200" },
  legacy: { text: "Existing values", cls: "bg-white/[0.04] border-white/10 text-slate-300" },
  manual: { text: "Entered by hand", cls: "bg-white/[0.04] border-white/10 text-slate-300" },
};

export default function PftHistoryEditor({
  tests,
  onChange,
  pftNA,
  naToggle,
  userEmail,
  inputCls,
  inputDateCls,
  labelCls,
  onError,
  // PFT Uploads page: the tests are exactly what was extracted, so no adding, removing
  // or N/A there; values can still be corrected.
  fixedList = false,
  // Each PFT date is a collapsible box (less scrolling). Closed, it shows a one-line
  // summary. startCollapsed: every box starts closed (PFT Uploads); otherwise only the
  // newest starts open (patient page).
  startCollapsed = false,
  // When given, a test whose report printed a different name says so.
  patientName = "",
  intro = "Every PFT on record, newest first. The newest one is used for study eligibility.",
  // PFT Uploads page: (test) => Map(key -> reason) of the boxes still to check. Those boxes
  // are outlined, and their PFT date, "More values" and "Other values" open on their own.
  reviewBoxes = null,
  // { testId, key, n }: open that test and scroll to that box (a new n each time).
  focus = null,
  // Patient page: show each PFT date's soft checks with Review + Verified. The address
  // lives on the patient, so its current value is passed in.
  softChecks = false,
  patientAddress = "",
  // Same for the DOB (a soft check when only the second look found it, 2026-10-27).
  patientDob = "",
  // PFT Uploads: (test, key, checked) saves a tick at once; softChecksLocked: read-only
  // (a card already moved, merged or dismissed).
  onVerify = null,
  softChecksLocked = false,
}) {
  const [showRemoved, setShowRemoved] = useState(false);
  const [openMore, setOpenMore] = useState({});
  const [opening, setOpening] = useState("");
  const [openOther, setOpenOther] = useState({});
  const [openTest, setOpenTest] = useState({});
  const boxesOf = (t) => (reviewBoxes && reviewBoxes(t)) || NO_BOXES;
  const autoOpened = useRef(new Set());

  // "Show" on a review item: open what holds the box, then scroll to it once it's drawn.
  const pendingFocus = useRef(null);
  useEffect(() => {
    if (!focus || !focus.testId) return;
    pendingFocus.current = focus;
    setOpenTest((m) => ({ ...m, [focus.testId]: true }));
    if (PFT_GROUPED_KEYS.includes(focus.key)) setOpenMore((m) => ({ ...m, [focus.testId]: true }));
    else setOpenOther((m) => ({ ...m, [focus.testId]: true }));
  }, [focus]);
  useEffect(() => {
    const f = pendingFocus.current;
    if (!f) return;
    const el = document.querySelector(`[data-pft-box="${f.testId}:${f.key}"]`);
    if (!el) return;
    pendingFocus.current = null;
    if (el.scrollIntoView) el.scrollIntoView({ block: "center", behavior: "smooth" });
    if (el.focus) el.focus({ preventScroll: true });
  });

  const visible = visibleTestsNewestFirst(tests);
  const removed = tests.filter(isRemoved);
  const newest = newestTest(tests);

  const update = (id, patch) => onChange(tests.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  const add = () =>
    onChange([
      ...tests,
      newManualTest({
        id: `manual_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
        now: new Date().toISOString(),
        by: userEmail,
      }),
    ]);
  const remove = (t) => {
    if (!window.confirm("Remove this PFT? It's hidden from the patient page but kept on the record, and can be restored.")) return;
    update(t.id, { removedAt: new Date().toISOString(), removedBy: userEmail || "" });
  };
  const restore = (t) => update(t.id, { removedAt: null, removedBy: null });
  const openPdf = async (t) => {
    setOpening(t.id);
    try {
      await openPftPdf(t.file);
    } catch (e) {
      if (onError) onError(e.message);
    } finally {
      setOpening("");
    }
  };

  // A value the two extraction readings disagreed on was left blank; say so, with
  // both readings, so a person can check the PDF and type the right one.
  const disagreedNote = (t, key) => {
    const r = t.disagreed && t.disagreed[key];
    if (!r || !blank(t[key])) return null;
    return (
      <span className="block text-[10px] text-amber-200 mt-0.5">
        Readings disagreed ({(r[0] || "blank")} / {(r[1] || "blank")}): check the PDF
      </span>
    );
  };
  // The readings disagreed but the value was settled automatically (the extraction job,
  // 2026-10-12): a Post predicted value repeating the Pre one, or the printed value when
  // the other reading had to calculate it. Shown while the value is still one of the two.
  const autoCheckedNote = (t, key) => {
    const a = t.autoChecked && t.autoChecked[key];
    if (!a || blank(t[key]) || !(a.readings || []).some((r) => String(r ?? "").trim() === String(t[key]).trim())) return null;
    return (
      <span className="block text-[10px] text-slate-400 mt-0.5">
        Readings differed ({(a.readings[0] || "blank")} / {(a.readings[1] || "blank")}); kept: {a.rule}
      </span>
    );
  };
  // The PDF doesn't print this value, so the extraction calculated it (decided
  // 2026-10-12). Shown while the value is still the calculated one.
  const calculatedNote = (t, key) => {
    const c = t.calculated && t.calculated[key];
    if (c === undefined || c === null || blank(t[key]) || String(c).trim() !== String(t[key]).trim()) return null;
    return <span className="block text-[10px] text-slate-400 mt-0.5">Calculated by RegX: not printed on the PDF</span>;
  };
  // A value only ONE reading found and nobody has verified yet (2026-10-27): its own calmer
  // outline, never blocking anything (a review box's amber outline wins when both apply).
  const oneReading = (t, key) =>
    softChecks && softChecksOf(t, { patientAddress, patientDob }).some((c) => c.key === key && c.oneReading && !c.verified);
  // A box with an unticked review item: outlined, with the reason under it.
  const boxCls = (t, key) =>
    boxesOf(t).has(key) ? `${inputCls} pft-review-box` : oneReading(t, key) ? `${inputCls} pft-one-reading-box` : inputCls;
  const oneReadingNote = (t, key) =>
    !boxesOf(t).has(key) && oneReading(t, key) ? (
      <span className="block text-[10px] mt-0.5 font-medium pft-one-reading-note">
        Only one reading found this: recheck against the PDF
      </span>
    ) : null;
  const reviewNote = (t, key) =>
    boxesOf(t).has(key) ? (
      <span className="block text-[10px] text-amber-200 mt-0.5 font-medium">To check: {boxesOf(t).get(key)}</span>
    ) : null;
  const valueInput = (t, key, placeholder) => (
    <>
      <input
        type="text"
        inputMode={TEXT_KEYS.has(key) ? undefined : "decimal"}
        value={t[key] ?? ""}
        placeholder={placeholder}
        onChange={(e) => update(t.id, { [key]: e.target.value })}
        className={boxCls(t, key)}
        aria-label={(field(key) || {}).label || key}
        data-pft-box={`${t.id}:${key}`}
        aria-invalid={boxesOf(t).has(key) || undefined}
      />
      {reviewNote(t, key)}
      {oneReadingNote(t, key)}
      {disagreedNote(t, key)}
      {autoCheckedNote(t, key)}
      {calculatedNote(t, key)}
    </>
  );
  const unitInput = (t, key) => (
    <input
      type="text"
      value={t[key] ?? ""}
      placeholder="unit"
      onChange={(e) => update(t.id, { [key]: e.target.value })}
      className={boxCls(t, key)}
      aria-label={(field(key) || {}).label || key}
      data-pft-box={`${t.id}:${key}`}
      aria-invalid={boxesOf(t).has(key) || undefined}
    />
  );
  // A date stored in another format (e.g. "03/01/2024" from the Sheet) is shown
  // as typed, never blanked by a date picker that can't read it.
  const dateInput = (t) =>
    blank(t.date) || ISO.test(String(t.date)) ? (
      <input
        type="date"
        value={t.date || ""}
        onChange={(e) => update(t.id, { date: e.target.value })}
        className={boxesOf(t).has("date") ? `${inputDateCls} pft-review-box` : inputDateCls}
        aria-label="PFT Date"
        data-pft-box={`${t.id}:date`}
      />
    ) : (
      <input
        type="text"
        value={t.date}
        onChange={(e) => update(t.id, { date: e.target.value })}
        className={boxCls(t, "date")}
        aria-label="PFT Date"
        data-pft-box={`${t.id}:date`}
      />
    );

  const groupBlock = (t, g) => (
    <div key={g.title} className="space-y-2">
      <span className="block text-[11px] font-semibold text-slate-200">{g.title}</span>
      {(g.pairs || []).map(([label, pre, post]) => pairRow(t, { label, pre, post }))}
      {(g.metrics || []).map((m) => (
        <div key={m.actual}>
          <span className={labelCls}>{m.label}</span>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[["Actual", m.actual], ["Predicted", m.predicted], ["% Predicted", m.pct]].map(([cap, key]) => (
              <label key={key} className="block">
                <span className="text-[10px] text-slate-500">{cap}</span>
                {valueInput(t, key)}
              </label>
            ))}
            <label className="block">
              <span className="text-[10px] text-slate-500">Unit</span>
              {unitInput(t, m.unit)}
            </label>
          </div>
        </div>
      ))}
      {(g.singles || []).length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {g.singles.map(([key, unitKey]) => (
            <label key={key} className="block">
              <span className={labelCls}>{field(key).label}</span>
              {unitKey ? (
                <div className="grid grid-cols-[1fr_6rem] gap-2">
                  <div>{valueInput(t, key)}</div>
                  {unitInput(t, unitKey)}
                </div>
              ) : (
                valueInput(t, key)
              )}
            </label>
          ))}
        </div>
      )}
    </div>
  );

  // Text read from the report (interpretation, bronchodilator, ...): a read-only note,
  // as printed. The PDF link above is always there to check it against.
  const reportNote = (t) => {
    const lines = PFT_NOTE_FIELDS.filter((k) => !blank(t[k]));
    if (!lines.length) return null;
    return (
      <div className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 space-y-1">
        <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400">From the report</span>
        {lines.map((k) => (
          <p key={k} className="text-[12px] text-slate-200 whitespace-pre-wrap break-words">
            <span className="text-slate-400">{field(k).label}: </span>
            {String(t[k])}
          </p>
        ))}
      </div>
    );
  };

  const pairRow = (t, p) => (
    <div key={p.label}>
      <span className={labelCls}>{p.label}</span>
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-[10px] text-slate-500">Pre</span>
          {valueInput(t, p.pre)}
        </label>
        <label className="block">
          <span className="text-[10px] text-slate-500">Post</span>
          {valueInput(t, p.post)}
        </label>
      </div>
    </div>
  );

  // "FEV1/FVC 65 · FEV1 70% · FVC 90%" (pre, else post), plus disagreements, for a closed box.
  const summary = (t) => {
    const v = (pre, post) => (!blank(t[pre]) ? t[pre] : t[post]);
    const parts = [
      ["FEV1/FVC", v("fev1fvcPre", "fev1fvcPost"), ""],
      ["FEV1", v("fev1Pre", "fev1Post"), "%"],
      ["FVC", v("fvcPre", "fvcPost"), "%"],
      ["DLCO", t.dlcoPct, "%"],
    ].filter(([, x]) => !blank(x)).map(([label, x, unit]) => `${label} ${x}${unit}`);
    const disagreed = Object.keys(t.disagreed || {}).filter((k) => blank(t[k])).length;
    const toCheck = boxesOf(t).size;
    const toVerify = softChecks ? softChecksOf(t, { patientAddress, patientDob }).filter((c) => !c.verified).length : 0;
    return [
      parts.join(" · ") || "No main values",
      disagreed ? `${disagreed} reading(s) disagreed` : "",
      toCheck ? `${toCheck} box(es) to check` : "",
      toVerify ? `${toVerify} to verify` : "",
    ].filter(Boolean).join(" · ");
  };

  // Values only one reading found: check them against the PDF and tick "Verified".
  const softPanel = (t) => {
    const list = softChecks ? softChecksOf(t, { patientAddress, patientDob }) : [];
    if (!list.length) return null;
    return (
      <div className="pft-soft-check rounded-lg border border-amber-400/30 bg-amber-500/[0.06] px-3 py-2 space-y-1.5">
        <span className="block text-[10px] uppercase tracking-wider font-medium text-amber-200">Check against the PDF</span>
        {list.map((c) => (
          <div key={c.key} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px]">
            <span className="text-slate-100">
              <span className="text-slate-400">{c.label}:</span> {c.value}
            </span>
            <span className="text-[11px] text-slate-400">{c.reason}</span>
            <span className="ml-auto flex items-center gap-2">
              {t.file && (
                <button
                  type="button"
                  onClick={() => openPdf(t)}
                  disabled={opening === t.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-[11px] text-slate-200 transition disabled:opacity-40"
                  aria-label={`Review ${c.label.toLowerCase()} on the PDF`}
                >
                  <ExternalLink className="w-3 h-3" />
                  Review
                </button>
              )}
              <label className="inline-flex items-center gap-1 text-[11px] text-slate-200">
                <input
                  type="checkbox"
                  checked={c.verified}
                  disabled={softChecksLocked}
                  onChange={(e) =>
                    onVerify
                      ? onVerify(t, c.key, e.target.checked)
                      : update(t.id, withVerification(t, c.key, { checked: e.target.checked, by: userEmail, now: new Date().toISOString() }))
                  }
                  className="accent-emerald-500"
                  aria-label={`${c.label} verified against the PDF`}
                />
                Verified
              </label>
            </span>
            {c.verified && (
              <span className="pft-soft-verified basis-full text-[10px] text-emerald-300">
                {c.decidedInMerge ? "Chosen in a merge" : "Verified"}{c.verifiedBy ? ` by ${c.verifiedBy}` : ""}
                {c.verifiedAt ? ` on ${String(c.verifiedAt).slice(0, 10)}` : ""}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  const testCard = (t) => {
    const badge = SOURCE_BADGE[t.source] || SOURCE_BADGE.manual;
    const boxes = boxesOf(t);
    // Opened because a box needed checking: stays open once that box is ticked, so it
    // never snaps shut under someone's hands (until they close it themselves).
    const auto = (part, needed) => {
      if (needed) autoOpened.current.add(`${t.id}:${part}`);
      return autoOpened.current.has(`${t.id}:${part}`);
    };
    const moreOpen = openMore[t.id] ?? (PFT_GROUPED_KEYS.some((k) => !blank(t[k])) || auto("more", PFT_GROUPED_KEYS.some((k) => boxes.has(k))));
    const isOpen = openTest[t.id] ?? (auto("test", boxes.size > 0) || (!startCollapsed && !!newest && newest.id === t.id));
    const others = otherValueKeys(t, [...boxes.keys()]);
    const otherOpen = openOther[t.id] ?? auto("other", others.some((k) => boxes.has(k)));
    return (
      <div key={t.id} className="rounded-xl border border-white/10 bg-white/[0.025] p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setOpenTest((m) => ({ ...m, [t.id]: !isOpen }))}
            aria-expanded={isOpen}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white uppercase tracking-wider hover:text-purple-200 transition"
          >
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            {blank(t.date) ? "PFT (no date)" : `PFT ${t.date}`}
          </button>
          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${badge.cls}`}>{badge.text}</span>
          {!fixedList && newest && newest.id === t.id && (
            <span className="text-[10px] px-2 py-0.5 rounded-full border bg-emerald-500/10 border-emerald-400/30 text-emerald-200">
              Newest: used for eligibility
            </span>
          )}
          <span className="ml-auto flex items-center gap-1.5">
            {t.file && (
              <button
                type="button"
                onClick={() => openPdf(t)}
                disabled={opening === t.id}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-[11px] text-slate-200 transition disabled:opacity-40"
                title={t.fileName || "Open the PDF this PFT came from"}
              >
                <ExternalLink className="w-3 h-3" />
                {opening === t.id ? "Opening…" : "Open PDF"}
              </button>
            )}
            {!fixedList && (
            <button
              type="button"
              onClick={() => remove(t)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-[11px] text-slate-300 transition"
            >
              <Trash2 className="w-3 h-3" />
              Remove
            </button>
            )}
          </span>
        </div>
        {patientName && t.nameOnReport && differentName(t.nameOnReport, patientName) && (
          <p className="text-[11px] text-amber-200">
            This report prints the name {t.nameOnReport}
            {t.dobOnReport ? ` (DOB ${t.dobOnReport})` : ""}
            {t.pages ? `, pages ${t.pages}` : ""}.
          </p>
        )}
        {(t.alsoIn || []).length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-400">
            <span>Same report also in:</span>
            {t.alsoIn.map((a) => (
              <button
                key={a.importId || a.file}
                type="button"
                onClick={() => openPdf({ ...t, id: `${t.id}:${a.file}`, file: a.file })}
                disabled={!a.file}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-slate-200 transition disabled:opacity-40"
                title="Open this copy of the PDF"
              >
                <ExternalLink className="w-3 h-3" />
                {a.fileName || "PDF"}
              </button>
            ))}
          </div>
        )}
        {!isOpen && <p className="text-[12px] text-slate-300">{summary(t)}</p>}

        {isOpen && (
        <>
        {softPanel(t)}
        <label className="block">
          <span className={labelCls}>PFT Date</span>
          {dateInput(t)}
          {reviewNote(t, "date")}
        </label>
        {PAIRS.map((p) => pairRow(t, p))}
        <div>
          <span className={labelCls}>FEV1 Reversibility</span>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex items-center gap-1">
              {valueInput(t, "revPercent")}
              <span className="text-xs text-slate-400 shrink-0">% change</span>
            </div>
            <div className="flex items-center gap-1">
              {valueInput(t, "revMl")}
              <span className="text-xs text-slate-400 shrink-0">ml</span>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setOpenMore((m) => ({ ...m, [t.id]: !moreOpen }))}
          className="inline-flex items-center gap-1 text-[11px] text-slate-300 hover:text-white transition"
          aria-expanded={moreOpen}
        >
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
          More values (volumes, flow, lung volumes, diffusion, height, weight, referring physician)
        </button>
        {moreOpen && <div className="space-y-4">{PFT_FIELD_GROUPS.map((g) => groupBlock(t, g))}</div>}
        {reportNote(t)}
        {others.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setOpenOther((m) => ({ ...m, [t.id]: !otherOpen }))}
              className="inline-flex items-center gap-1 text-[11px] text-slate-300 hover:text-white transition"
              aria-expanded={otherOpen}
            >
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${otherOpen ? "rotate-180" : ""}`} />
              Other values from this report ({others.length})
            </button>
            {otherOpen && (
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {others.map((key) => (
                  <label key={key} className="block">
                    <span className={labelCls}>{field(key).label}</span>
                    {valueInput(t, key)}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
        </>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-[11px] text-slate-400">{intro}</p>
        <span className="ml-auto flex items-center gap-2">
          {naToggle}
          {!pftNA && !fixedList && (
            <button
              type="button"
              onClick={add}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-xs text-slate-200 transition"
            >
              <Plus className="w-3.5 h-3.5" />
              Add PFT date
            </button>
          )}
        </span>
      </div>

      {!pftNA && (
        <>
          {visible.length === 0 ? (
            <p className="text-xs text-slate-400 flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5" />
              No PFT recorded yet.
            </p>
          ) : (
            visible.map(testCard)
          )}

          {!fixedList && removed.length > 0 && (
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setShowRemoved((s) => !s)}
                className="text-[11px] text-slate-400 hover:text-white transition"
              >
                {showRemoved ? "Hide" : "Show"} removed PFTs ({removed.length})
              </button>
              {showRemoved &&
                removed.map((t) => (
                  <div
                    key={t.id}
                    className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-[12px] text-slate-400"
                  >
                    {blank(t.date) ? "PFT (no date)" : `PFT ${t.date}`}
                    <button
                      type="button"
                      onClick={() => restore(t)}
                      className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-[11px] text-slate-200 transition"
                    >
                      <RotateCcw className="w-3 h-3" />
                      Restore
                    </button>
                  </div>
                ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
